import { ReactNode } from 'react';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ListItemIcon, ListItemText, Menu } from '@mui/material';

import { getContextMenuPaperSx } from '../contextMenuDesign';
import { ContextMenuItem } from './tableContextMenu.styles';
import useSubmenuHover from './useSubmenuHover';

interface Props {
  icon: ReactNode;
  label: string;
  testId: string;
  /** Closes the submenu and the context menu that owns it. */
  onAfterAction: () => void;
  children: (close: () => void) => ReactNode;
}

/**
 * Trigger row plus the nested menu it opens on hover.
 *
 * A nested MUI Menu is a Modal: it renders a full-viewport root (and backdrop)
 * that paints above the parent menu. Left hit-testable, those layers cover the
 * trigger the pointer is resting on, so the browser re-targets the stationary
 * pointer and fires mouseleave on the trigger — closing the submenu, uncovering
 * the trigger, firing mouseenter, and reopening it, forever. Dropping the
 * backdrop and making the root pointer-transparent (paper excepted) leaves the
 * trigger hoverable underneath.
 */
const ContextSubmenu = ({ icon, label, testId, onAfterAction, children }: Props) => {
  const { anchorEl, open, openSubmenu, closeSubmenu, scheduleClose, cancelClose } =
    useSubmenuHover();

  const close = () => {
    closeSubmenu();
    onAfterAction();
  };

  return (
    <>
      <ContextMenuItem
        onMouseEnter={openSubmenu}
        onMouseLeave={scheduleClose}
        onClick={openSubmenu}
        data-testid={testId}
      >
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText>{label}</ListItemText>
        <ChevronRightIcon fontSize="small" />
      </ContextMenuItem>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeSubmenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        hideBackdrop
        sx={{ pointerEvents: 'none' }}
        slotProps={{
          paper: { sx: [getContextMenuPaperSx, { pointerEvents: 'auto' }] },
        }}
        MenuListProps={{ onMouseEnter: cancelClose, onMouseLeave: scheduleClose }}
      >
        {children(close)}
      </Menu>
    </>
  );
};

export default ContextSubmenu;
